import { useRef, useState } from 'react';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';

type Props = {
  options: {
    id: number;
    label: string;
    type?: string;
  }[];
  selectedOption: number;
  setSelectedOption: (selectedOption: { id: number; label: string }) => void;
  disabled?: boolean;
  title?: string | Element;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
};

/**
 * A button component that displays a dropdown list of options.
 *
 * @param {Object} Props
 * @param {Array} Props.options - An array of options to display in the dropdown.
 * Each option should have an `id` and a `label`.
 * @param {number} Props.selectedOption - The id of the currently selected option.
 * @param {Function} Props.setSelectedOption - A function to update the selected option.
 * @param {boolean} Props.disabled - A boolean indicating whether the button is disabled. Defaults to false.
 * @param {string} Props.title - The title to display on the button. If not provided,
 * the label of the selected option will be shown
 * @param {string} [Props.size="small"] - The size of the button. Can be 'small', 'medium', or 'large'. Defaults to 'medium'.
 * @param {string} [Props.variant="text"] - The variant of the button. Can be 'text', 'outlined', or 'contained'.
 * Defaults to 'text'.
 *
 * @returns {JSX.Element} A button that toggles a dropdown menu with the provided options.
 */
export function DropDownSelectButton({
  options,
  selectedOption,
  setSelectedOption,
  disabled = false,
  title = '',
  size = 'medium',
  variant = 'text'
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        disabled={disabled}
        endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={handleToggle}
        ref={anchorRef}
        size={size}
        variant={variant}
      >
        {title || options.find((option) => option.id === selectedOption)?.label}
      </Button>
      <Popper
        anchorEl={anchorRef.current}
        open={open}
        style={{ zIndex: '99999' }}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  {options.map((option) => (
                    <MenuItem
                      key={option.id}
                      onClick={() => setSelectedOption(option)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export default DropDownSelectButton;
